import { styled } from "@mui/material/styles";
import { useState } from "react";

/**
 * Percent of image to crop.
 * This is helpful with tidying up images we get that have a lot of whitespace.
 */
const CROP = 0.4;

type Industry = Extract<
  GQL.VocationIndustry,
  "BUSINESS_AND_OFFICE" | "HEALTH" | "TRADE_AND_INDUSTRY" | "TECHNICAL_AND_COMMUNICATIONS"
>;
const industryToFolder: Record<Industry, string> = {
  BUSINESS_AND_OFFICE: "business",
  HEALTH: "health",
  TRADE_AND_INDUSTRY: "trade",
  TECHNICAL_AND_COMMUNICATIONS: "technical",
};

const isValidIndustry = (industry?: GQL.VocationIndustry | null): industry is Industry =>
  !!industry && Object.keys(industryToFolder).includes(industry);

export type Persona = "drill_sergeant" | "friendly" | "professional";

// Provides a persona and industry lookup and returns the appropriate coach image
// with the proper resizing and styles applied.
export const Coach = ({
  size,
  loading,
  industry,
  persona,
  style,
}: {
  size: number;
  loading?: boolean;
  industry?: GQL.VocationIndustry | null;
  persona?: Persona;
  style?: React.CSSProperties;
}) => {
  return (
    <CoachImage
      size={size}
      loading={loading}
      style={style}
      src={`${process.env.DREAM_PORTAL_URL}/images/coach/${isValidIndustry(industry) ? `${industryToFolder[industry]}/` : ""}${persona ? persona : "coach"}.svg`}
    />
  );
};

// Returns a coach image (from the given source) with the proper resizing and styles applied.
export const CoachImage = ({
  size,
  loading,
  style,
  src,
  imgProps,
}: {
  size: number;
  src: string;
  loading?: boolean;
  style?: React.CSSProperties;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
}) => {
  const [imgSrc, setImgSrc] = useState<string | undefined>(src);
  return (
    <CropContainer
      style={{ width: size, height: size, ...style }}
      className={loading ? "loading" : ""}
    >
      <CoachImg
        size={size}
        src={imgSrc}
        alt="coach"
        onError={() => setImgSrc(`${process.env.DREAM_PORTAL_URL}/images/coach/coach.svg`)}
        {...imgProps}
      />
    </CropContainer>
  );
};

const CropContainer = styled("div")<{ loading?: boolean }>`
  position: relative;
  overflow: hidden;

  @keyframes penguin-dance {
    0% {
      transform: translateY(0) rotate(0deg);
    }
    10% {
      transform: translateY(3px) rotate(3deg);
    }
    20% {
      transform: translateY(9px) rotate(9deg);
    }
    25% {
      transform: translateY(10px) rotate(10deg);
    }
    30% {
      transform: translateY(9px) rotate(9deg);
    }
    40% {
      transform: translateY(3px) rotate(3deg);
    }
    50% {
      transform: translateY(0) rotate(0deg);
    }
    60% {
      transform: translateY(3px) rotate(-3deg);
    }
    70% {
      transform: translateY(9px) rotate(-9deg);
    }
    75% {
      transform: translateY(10px) rotate(-10deg);
    }
    80% {
      transform: translateY(9px) rotate(-9deg);
    }
    90% {
      transform: translateY(3px) rotate(-3deg);
    }
    100% {
      transform: translateY(0) rotate(0deg);
    }
  }

  &.loading {
    animation: penguin-dance 1.2s infinite;
    transform-origin: 50% 70%; /* Pivot point at the bottom center */
  }
`;
export const CoachImg = styled("img")<{ size: number }>`
  position: absolute;
  transform: ${({ size }) => `translate(-${(CROP / 2) * size}px, -${(CROP / 2) * size}px)`};
  width: ${({ size }) => size + CROP * size}px;
  height: ${({ size }) => size + CROP * size}px;
`;
